






















import Vue from 'vue';
import Component from 'vue-class-component';
import { InjectReactive } from 'vue-property-decorator';

// Shared types
import { System, Brand } from '@coldtec/cos-types';

// Store types
import { SpacesAction, ToolSystem, ToolUnit } from '@/store/store.types';

// Component for rendering the unit of a system
import SystemUnit from './SystemUnit.vue';

/**
 *  Component that is responsible for rendering the units of a system.
 */
@Component({
    components: {
        SystemUnit,
    },
})
export default class SystemUnits extends Vue {

    @InjectReactive('system')
    private system!: System;

    @InjectReactive('toolSystem')
    private toolSystem!: ToolSystem;

    /**
     *
     *  Used to create a unique key for a unit that
     *  is rendered for the system.
     *
     *  (i) Needed because a single unit can be rendered
     *  at a different system, letting Vue throw an
     *  error of duplicate keys.
     *
     *  @param unit The unit to get the unique key for
     *  @param index The index of the unit for this system
     *
     *  @returns A unique key to be used for the v-for
     *
     */
    private getKey(unit: ToolUnit, index: number): string {

        return `${ this.system.guid }-${ unit.data.guid }-${ index }`;

    }

    /**
     *
     *  Used to get the title for the unit, which is a combination
     *  of the system's brand and the unit's SKU.
     *
     *  @param unit The unit to get the title for
     *
     *  @returns The title for the unit's tile
     *
     */
    private getTitle(unit: ToolUnit): string {

        return `De ${ (this.system.brand as Brand).name } ${ unit.data.sku }`;

    }

    /**
     *
     *  Used to dispatch an action to save the space's indoor
     *  unit color when another color was selected.
     *
     *  @param unit The tool unit of which the color was updated
     *
     */
    private colorUpdate(unit: ToolUnit): void {

        this.$store.dispatch(SpacesAction.SELECT_INDOOR_UNIT_COLOR, unit);

    }

    /**
     *
     *  Used to act upon a unit's space selection: dispatching
     *  an event to update the space's `indoor_unit` property
     *  and selecting or resetting the other unit's spaces
     *  according to the possible layouts.
     *
     *  @param unit The tool unit of which the space was updated
     *
     */
    private spaceUpdate(unit: ToolUnit): void {

        /**
         *  Dispatch the action to update the space's `indoor_unit` property.
         */
        this.$store.dispatch(SpacesAction.SELECT_INDOOR_UNIT, unit);

        /**
         *  Loop through every indoor unit to check if their space should be updated.
         */
        for (const indoorUnit of this.toolSystem.indoorUnits) {

            /**
             *  Skip checking himself to prevent an infinite loop.
             */
            if (indoorUnit.localId === unit.localId) {

                continue;

            }

            /**
             *  Check and update the unit for which the space was selected before.
             */
            if (!indoorUnit.toolSpaceId || indoorUnit.toolSpaceId !== unit.toolSpaceId) {

                continue;

            }

            /**
             *  Holding the new tool space id of the unit.
             */
            let newToolSpaceId = null;

            /**
             *  Check if the unit is present in one of the
             *  layouts - if present - to automatically update
             *  his space if possible.
             */
            for (const layout of this.toolSystem.layouts || []) {

                /**
                 *  Only check layouts that contain this indoor unit.
                 */
                if (!layout.indoor_units.includes(indoorUnit.guid)) {

                    continue;

                }

                /**
                 *  Only check if the space is still available if
                 *  it's not about the space that just got set.
                 */
                if (layout.tool_space_id === unit.toolSpaceId) {

                    continue;

                }

                /**
                 *  Check if the space wasn't already used for another unit.
                 */
                let alreadySelected = false;

                for (const indoorUnitCheck of this.toolSystem.indoorUnits) {

                    if (indoorUnitCheck.toolSpaceId !== layout.tool_space_id) {

                        continue;

                    }

                    alreadySelected = true;

                    break;

                }

                if (alreadySelected) {

                    continue;

                }

                /**
                 *  Use this space for the unit if it's not
                 *  already selected for another unit.
                 */
                newToolSpaceId = layout.tool_space_id;

            }

            /**
             *
             *  Update the local space id of the unit.
             *
             *  (i) The unit component automagically acts upon setting
             *  this property, because it's bound to his space
             *  selection input and it contains a watcher.
             *
             */
            indoorUnit.toolSpaceId = newToolSpaceId;

        }

        /**
         *
         *  Also ensure the possibly already selected
         *  color is set for the space.
         *
         *  (i) Because we want to allow users to browse
         *  the colors before selecting a space, the
         *  color is first bound to the `ToolUnit` object.
         *
         */
        this.colorUpdate(unit);

    }

}
